import ReactDOM from "react-dom/client";
import "./main.css";
import React from "react";
import { Center, MantineProvider } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { createRouter } from "@tanstack/react-router";
import {
  QueryCache,
  QueryClient,
  QueryClientProvider
} from "@tanstack/react-query";
import { ReactPlugin as AppInsightsReactPlugin } from "@microsoft/applicationinsights-react-js";
import globalTheme from "./theme";
import { routeTree } from "./routeTree.gen";
import { AuthProvider } from "./providers/AuthProvider";
import { AppInsightsProvider } from "./providers/AppInsightsProvider";
import { HookedRouterProvider } from "./routes/-HookedRouterProvider";
import LoadingPane from "./components/LoadingPane";
import { showError } from "./notifications/Notifications";

const appInsightsReactPlugin = new AppInsightsReactPlugin();
const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Skip silenced queries (opt out with meta.suppressErrorNotification)
      if (query.meta?.suppressErrorNotification) {
        return;
      }
      const description =
        query.meta?.errorMessage ?? "An unexpected error occurred.";
      showError(
        `${description instanceof Function ? description(error) : description}`,
        "Error"
      );
    }
  })
});
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultStaleTime: 5000,
  scrollRestoration: true,
  defaultPendingComponent: () => (
    <Center>
      <LoadingPane />
    </Center>
  ),
  context: {
    appInsightsReactPlugin: appInsightsReactPlugin,
    queryClient: queryClient,
    userClaim: undefined!, // set in/by HookedRouterProvider
    apiHook: undefined! // set in/by HookedRouterProvider
  }
});

// Register things for typesafety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: {
      /** Custom error message shown in the global error notification.
       *  Can be a string or a function that receives the error. */
      errorMessage?: string | ((error: Error) => string);
      /** Set to true to suppress the global error notification for this query. */
      suppressErrorNotification?: boolean;
    };
  }
}
const rootElement = document.getElementById("root")!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <AppInsightsProvider appInsightsReactPlugin={appInsightsReactPlugin}>
        <MantineProvider theme={globalTheme}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <Notifications />
              <HookedRouterProvider router={router} />
            </AuthProvider>
          </QueryClientProvider>
        </MantineProvider>
      </AppInsightsProvider>
    </React.StrictMode>
  );
}
